import React, { useId } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { sendAllMessages as s } from './sendAllMessages';
import type { SendAllState } from './useSendAll';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Send to all sheet; each part uses its own slice of it */
import styles from './SendAll.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The rights line and "Keep my profiles in sync" (design c `.opt`). The rights line is asked once per
 * artist: after the first send it shows ticked and cannot change. The switch needs the stored rights
 * confirmation (the server sends an automatic send with it).
 */
const SendAllOptionsComponent: React.FC<{ state: SendAllState }> = ({ state }) => {
  const intl = useIntl();
  const rightsId = useId();
  const syncId = useId();
  const { rightsAccepted, rightsTicked, setRightsTicked, keepInSync, setKeepInSync, isSavingSettings } = state;
  let syncSub = intl.formatMessage(keepInSync ? s.syncOn : s.syncOff);
  if (!rightsAccepted && !keepInSync) {
    syncSub = intl.formatMessage(s.syncNeedsRights);
  }

  return (
    <>
      <label className={styles.option} htmlFor={rightsId} data-testid="send-all-rights">
        <input
          id={rightsId}
          type="checkbox"
          className={styles.tick}
          checked={rightsAccepted || rightsTicked}
          disabled={rightsAccepted}
          onChange={(event) => setRightsTicked(event.target.checked)}
          data-testid="send-all-rights-tick"
        />
        <span className={styles.optionText}>
          <span className={styles.optionLabel}>{intl.formatMessage(s.rights)}</span>
          <span className={styles.optionSub}>{intl.formatMessage(s.rightsOnce)}</span>
        </span>
      </label>
      <label className={styles.option} htmlFor={syncId} data-testid="send-all-sync">
        <span className="form-check form-switch m-0">
          <input
            id={syncId}
            type="checkbox"
            role="switch"
            className="form-check-input m-0"
            checked={keepInSync}
            disabled={isSavingSettings || (!rightsAccepted && !keepInSync)}
            onChange={(event) => setKeepInSync(event.target.checked)}
            data-testid="send-all-sync-switch"
          />
        </span>
        <span className={styles.optionText}>
          <span className={styles.optionLabel}>{intl.formatMessage(s.sync)}</span>
          <span className={styles.optionSub}>{syncSub}</span>
        </span>
      </label>
    </>
  );
};

export const SendAllOptions = TypedMemo(SendAllOptionsComponent);
